import { useEffect, useMemo, useRef, useState } from 'react';

import { Input } from './input';

export type LookupOption = { label: string; value: string };

type LookupAutocompleteProps = {
  value: string;
  onTextChange: (next: string) => void;
  onSelect: (option: LookupOption) => void;
  options: LookupOption[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onSearch?: (query: string) => void;
  loadingText?: string;
  emptyText?: string;
};

export function LookupAutocomplete({
  value,
  onTextChange,
  onSelect,
  options,
  loading,
  disabled,
  placeholder,
  onSearch,
  loadingText = 'Cargando…',
  emptyText = 'Sin resultados',
}: LookupAutocompleteProps) {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [search, setSearch] = useState(value || '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const skipOpenRef = useRef(false);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  const filtered = useMemo(
    () =>
      options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase())),
    [options, search],
  );

  const handleSelect = (option: LookupOption) => {
    skipOpenRef.current = true;
    onSelect(option);
    setSearch(option.label);
    setOpenDropdown(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={search}
        placeholder={placeholder}
        onFocus={() => {
          if (skipOpenRef.current) {
            skipOpenRef.current = false;
            return;
          }
          onSearch?.(search);
          setOpenDropdown(true);
        }}
        onChange={(event) => {
          const next = event.target.value;
          setSearch(next);
          onTextChange(next);
          onSearch?.(next);
          setOpenDropdown(true);
        }}
        onBlur={() => {
          setTimeout(() => setOpenDropdown(false), 120);
        }}
        autoComplete="off"
        disabled={disabled}
      />
      {openDropdown ? (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {loading ? <div className="px-3 py-2 text-sm text-gray-500">{loadingText}</div> : null}
          {!loading && filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyText}</div>
          ) : (
            filtered.map((option) => (
              <button
                type="button"
                key={option.value}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
